import { DEFAULT_EVENT_DURATION_MINUTES } from '@sixam/shared/event-flow-constants';
import { systemNow } from '@sixam/shared/clock';
import type { Event } from '@/lib/events';

export type IcsCalendarEvent = Pick<
  Event,
  'description' | 'ends_at' | 'id' | 'place_address' | 'place_name' | 'starts_at' | 'title'
>;

export function icsCalendarFileName(event: Pick<IcsCalendarEvent, 'title'>): string {
  const slug = event.title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return `${slug || 'event'}.ics`;
}

export function icsCalendarContent(event: IcsCalendarEvent, now = systemNow()): string {
  const end =
    event.ends_at ??
    new Date(
      new Date(event.starts_at).getTime() + DEFAULT_EVENT_DURATION_MINUTES * 60 * 1000
    ).toISOString();
  const location = [event.place_name, event.place_address].filter(Boolean).join(' · ');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//6am BKK//Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.id}@6ambkk`,
    `DTSTAMP:${formatIcsDate(now.toISOString())}`,
    `DTSTART:${formatIcsDate(event.starts_at)}`,
    `DTEND:${formatIcsDate(end)}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    `DESCRIPTION:${escapeIcsText(event.description ?? '')}`,
    `LOCATION:${escapeIcsText(location)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return `${lines.map(foldIcsLine).join('\r\n')}\r\n`;
}

function formatIcsDate(value: string): string {
  return new Date(value)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z');
}

function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

function foldIcsLine(line: string): string {
  if (line.length <= 75) {
    return line;
  }

  const chunks: string[] = [];
  let remaining = line;
  while (remaining.length > 75) {
    chunks.push(remaining.slice(0, 75));
    remaining = remaining.slice(75);
  }
  chunks.push(remaining);
  return chunks.join('\r\n ');
}
